import * as ramotswe from "./ramotswe";
import { DateTime } from "../imports";
import { parseRamotsweDatetime, getEarliestDate } from "../utils";

/**
 * Definitions for how observatory data will be stored
 *
 * The data for an observatory is "chunked" into a few different objects, roughly
 * following usage and the format of results from Ramotswe.
 *
 * Data chunks are either *_snapshot or *_history

 * snapshot chunks contain a exportDatetime attribute which is the datetime_utc returned
 * by Ramotswe, as the last known data collection time.
 * exportDatetime is distinct from injestDatetime, which is when this data was recieved
 * by us
 *
 */

// Functions used to update the data
// Could remove these as separate functions and write them directly into the
// DataSubset constructor
async function allSkyImageUpdater(observatoryName) {
  return await ramotswe.getAllSkyImage(observatoryName);
}
async function fodImageUpdater(observatoryName) {
  return await ramotswe.getImage(
    "observatoryimage",
    observatoryName,
    "frontofdome"
  );
}
async function statusSnapshotUpdater(observatoryName) {
  return await ramotswe.getMuscaData(observatoryName);
}
async function weatherSnapshotUpdater(observatoryName) {
  return await ramotswe.getCumulusData(observatoryName);
}
async function visibilitySnapshotUpdater(observatoryName) {
  return await ramotswe.getCloudwatcherData(observatoryName);
}
async function powerSnapshotUpdater(observatoryName) {
  const statusInfo = await ramotswe.getBatteryState(observatoryName);
  const chargeInfo = await ramotswe.getBatteryCharge(observatoryName);

  const datetimes = [
    statusInfo?.exportDatetime,
    chargeInfo?.exportDatetime,
  ].map((dt_string) => DateTime.fromISO(parseRamotsweDatetime(dt_string)));

  return {
    battery_state_of_charge: chargeInfo?.battery_state_of_charge,
    battery_state: statusInfo?.battery_state,
    exportDatetime: getEarliestDate(datetimes).toISO(),
  };
}
async function astronomicalSnapshotUpdater(observatoryName) {
  const horizonRelative = await ramotswe.getSunMoonVisible(observatoryName);
  const sunMoonAltitudes = await ramotswe.getSunMoonAltitudes(observatoryName);
  const sunTimes = await ramotswe.getSunTimings(observatoryName);
  const moonPhase = await ramotswe.getMoonPhase(observatoryName);

  return {
    sun_visibility: horizonRelative?.sun_visibility,
    moon_visibility: horizonRelative?.moon_visibility,
    sun_altitude: sunMoonAltitudes?.sun,
    moon_altitude: sunMoonAltitudes?.moon,
    next_sunset: sunTimes?.next_sunset,
    next_sunrise: sunTimes?.next_sunrise,
    sun_time_until: sunTimes?.time_until,
    sun_next_event: sunTimes?.next_event,
    moon_phase: moonPhase?.moon_phase,
    moon_illumination: moonPhase?.moon_illumination,
  };
}

/**
 * Data chunks are stored as DataSubset objects, which live inside observatory objects
 * DataSubsets contain arbitrary attributes (the data we actually want), an
 * `injestDatetime` attribute, and an update() method.
 */
class DataSubset {
  /**
   * @param {object} data - the initializing data
   * @param {function} updateFunction - a function to call to update the data - this
   * should return something the same shape as `data`
   */
  constructor(data, updateFunction) {
    // apply the elements of `data` as attributes of this class
    for (let [key, value] of Object.entries(data)) {
      this[key] = value;
    }

    // set "injestDatetime"
    this.injestDatetime = new Date();

    // store the updater function for .update()
    this.updateFunction = updateFunction;
  }

  async update() {
    // fetch the new data, using the instructions given on class instantiation
    const updatedData = await this.updateFunction();

    // apply updated data
    for (let [key, value] of Object.entries(updatedData)) {
      if (Object.hasOwn(this, key)) {
        this[key] = value;
      } else {
        console.error(
          `Unknown property ${key} - known properties: ${Object.keys(this)}`
        );
      }
    }

    this.injestDatetime = new Date();
  }
}

/**
 * Each observatory will have an ObservatoryData object
 */
export class ObservatoryData {
  constructor(observatoryName) {
    this.name = observatoryName;

    /**
     * These are the different "chunks" of data
     * For each chunk x, a DataSubset is created with initial null data,
     * and updater function xUpdater, which has been pre-populated with the
     * observatory name
     */
    this.allSkyImage = new DataSubset(
      { blobURL: null, exportDatetime: null },
      () => allSkyImageUpdater(observatoryName)
    );
    this.frontOfDomeImage = new DataSubset(
      { blobURL: null, exportDatetime: null },
      () => fodImageUpdater(observatoryName)
    );

    /**
     * Snapshot chunks
     */
    this.statusSnapshot = new DataSubset(
      {
        exportDatetime: null,
        action: null,
        dome_position: null,
        door_position: null,
        battery_voltage: null,
      },
      () => statusSnapshotUpdater(observatoryName)
    );

    this.weatherSnapshot = new DataSubset(
      {
        exportDatetime: null,
        temperature: null,
        humidity: null,
        wind_speed_moving_average: null,
        rainfall_today: null,
        pressure_sea_level: null,
        current_wind: null,
      },
      () => weatherSnapshotUpdater(observatoryName)
    );

    this.visibilitySnapshot = new DataSubset(
      { exportDatetime: null, clarity: null, darkness: null, dryness: null },
      () => visibilitySnapshotUpdater(observatoryName)
    );

    this.powerSnapshot = new DataSubset(
      {
        exportDatetime: null,
        battery_state_of_charge: [null],
        battery_state: null,
      },
      () => powerSnapshotUpdater(observatoryName)
    );

    this.astronomicalSnapshot = new DataSubset(
      {
        sun_visibility: null,
        moon_visibility: null,
        sun_altitude: null,
        moon_altitude: null,
        next_sunrise: null,
        next_sunset: null,
        sun_time_until: null,
        sun_next_event: null,
        moon_phase: null,
        moon_illumination: null,
      },
      () => astronomicalSnapshotUpdater(observatoryName)
    );
  }
}
